import axios from "./httpClient";
import * as _ from "lodash";

import { ApiType } from "./Types";
import { getUrl } from "./getURL";
import { ErrorMessages, STORAGE_KEYS } from "../constants";
import { getTrackingHeadersMap } from "./getTrackingHeaders";
import { IAuthUser } from "../models/user";

/**
 * Errors that should trigger Okta re-login (authentication issues)
 * All other errors (server errors, SQL errors) should NOT trigger Okta redirect
 */
const RETRYABLE_AUTH_ERROR_PATTERNS = [
  "user is not active",
  "no token provided",
  "jwt expired",
];

/**
 * Check if error is a retryable auth error that should trigger Okta redirect
 */
export const isRetryableAuthError = (error: any): boolean => {
  const errorMessage = (
    error?.response?.data?.error?.message ??
    error?.response?.data?.errors?.[0]?.message ??
    error?.message ??
    ""
  ).toLowerCase();
  
  return RETRYABLE_AUTH_ERROR_PATTERNS.some(pattern => errorMessage.includes(pattern));
};

/**
 * Validate Okta token with backend (single attempt, no retries)
 * @param providedToken - Okta ID token from tokenManager
 * @returns - Logged in User details type of IAuthUser
 * @throws Error with isServerError flag for non-auth errors
 */
export const validateOktaToken = async (providedToken?: string): Promise<IAuthUser> => {
  const token = providedToken ?? getTokenFromStorage();
  
  if (!token) {
    throw new Error("No Okta token available");
  }
  
  return validateToken(token);
};

// Get token from localStorage (no retry)
const getTokenFromStorage = (): string | null => {
  const user = JSON.parse(localStorage.getItem(STORAGE_KEYS.OKTA_TOKEN_STORAGE) ?? "null");
  return _.get(user, "idToken.idToken") ?? null;
};

// Single attempt token validation
const validateToken = async (token: string): Promise<IAuthUser> => {
  try {
    const staticHeaders = getTrackingHeadersMap(ApiType.VALIDATE_OKTA_TOKEN);
    const headers = { ...staticHeaders };
    const response = await axios.post(
      getUrl(ApiType.VALIDATE_OKTA_TOKEN),
      { token },
      { headers }
    );
    return response.data;
  } catch (error: any) {
    console.error("Token validation failed:", error);
    
    const status = _.get(error, "response.status");
    const errorMessage = getErrorMessage(error);
    
    // Create error with metadata for proper handling
    const enhancedError = new Error(errorMessage) as Error & { 
      isServerError?: boolean; 
      isRetryableAuthError?: boolean;
      status?: number;
    };
    enhancedError.isServerError = status >= 500;
    enhancedError.isRetryableAuthError = isRetryableAuthError(error);
    enhancedError.status = status;
    
    throw enhancedError;
  }
};

// Extracted error handling logic
const getErrorMessage = (error: any): string => {
  const status = _.get(error, "response.status");
  if (!status) return ErrorMessages.statusCodeNotFound;
  if (status === 503) return ErrorMessages.deploymentInprogressError;
  
  // Get raw error message
  const rawMessage = 
    _.get(error, "response.data.error.message") ??
    _.get(error, "response.data.errors[0].message") ??
    "";
  
  // Handle IBM AS400 database errors with user-friendly message
  if (rawMessage.includes("[IBM][CLI Driver][AS]")) {
    return "AS400 Database Error: We couldn't process your request. Please try again later.";
  }
  
  if (status === 500) return ErrorMessages.serverError;
  return rawMessage || ErrorMessages.validateOktaToken;
};
